import { memo } from "react";
import { Handle, Position } from "reactflow";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import type { RoadmapNode } from "../../../data/ch_concepts";
import { STATUS_COLORS, NODE_SPRING } from "./constants";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface TopicsGroupNodeData {
  type: "topicsGroup";
  topics: RoadmapNode[];
  areaLabel: string;
  areaDescription: string;
  categoryAccent: string;
  onTopicClick: (node: RoadmapNode, areaLabel: string, areaDescription: string) => void;
}

interface Props {
  data: TopicsGroupNodeData & { revealDelay?: number };
}

// ─── Component ─────────────────────────────────────────────────────────────────

function TopicsGroupNodeInner({ data }: Props) {
  const { topics, revealDelay = 0 } = data;

  return (
    <div>
      <Handle type="target" id="left" position={Position.Left} className="!bg-transparent !border-0 !w-1.5 !h-3 !min-w-0" />

      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ ...NODE_SPRING, delay: revealDelay }}
      >
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {topics.map((topic, idx) => {
              const sc = STATUS_COLORS[topic.status] ?? STATUS_COLORS.to_learn;
              const isKnown = topic.status === "known";
              const isNext = topic.status === "next";

              return (
                <div
                  key={topic.id}
                  data-topic-index={idx}
                  style={{ cursor: "pointer" }}
                  className="flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  {/* Status dot */}
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0 border-2"
                    style={{
                      backgroundColor: isKnown || isNext ? sc.dot : "white",
                      borderColor: sc.dot,
                      ...(isNext ? { boxShadow: `0 0 0 3px ${sc.dot}25` } : {}),
                    }}
                  />

                  {/* Label */}
                  <span
                    className="text-[13px] font-medium leading-snug flex-1 min-w-0"
                    style={{ color: sc.text }}
                  >
                    {topic.label}
                  </span>

                  {/* Status badge */}
                  {isNext && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-blue-200 text-blue-600 flex-shrink-0 whitespace-nowrap">
                      Изучить
                    </span>
                  )}
                  {isKnown && (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export const TopicsGroupNode = memo(TopicsGroupNodeInner);
